import { Link, useNavigate } from 'react-router-dom';
import { useWorkflows } from '../context/WorkflowsContext';
import { useToolbox } from '../context/ToolboxContext';
import { useTabs } from '../context/TabContext';

export function Workflows() {
  const { workflows, removeWorkflow } = useWorkflows();
  const { loadToolsFromIds } = useToolbox();
  const { setActiveTab } = useTabs();
  const navigate = useNavigate();

  const handleOpenWorkflow = (workflow: typeof workflows[number]) => {
    loadToolsFromIds(workflow.tools.map(t => t.id));
    navigate('/');
    setActiveTab('toolbox');
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 pt-4 pb-6 font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-800 font-heading">Workflows Created</h1>
        <p className="text-sm text-neutral-500">
          Organize tool stacks you've built for specific workflows during this session.
        </p>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-800 font-heading">Your Workflows</h2>
            <p className="text-xs text-neutral-500">
              {workflows.length === 0
                ? 'No Workflows Saved Yet'
                : `${workflows.length} workflow${workflows.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <Link
            to="/"
            className="btn-primary"
          >
            Begin Workflow
          </Link>
        </div>

        {workflows.length === 0 ? (
          <div className="border border-dashed border-neutral-300 rounded-card p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary-50 text-primary-600 mx-auto flex items-center justify-center mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-800 mb-2 font-heading">No Workflows Yet</h3>
            <p className="text-sm text-neutral-500 max-w-md mx-auto">
              Use the chat and toolbox to build a workflow. When you're ready, save it from the Toolbox tab.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflows.map((workflow) => (
              <div key={workflow.id} className="card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-neutral-800 font-heading">{workflow.name}</h3>
                    <p className="text-xs text-neutral-500 mt-1">
                      {workflow.tools.length} tools • Created {new Date(workflow.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-pill">
                    Draft
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {workflow.tools.map((tool) => (
                    <span
                      key={`${workflow.id}-${tool.id}`}
                      className="text-xs text-neutral-600 bg-neutral-100 px-2 py-1 rounded-pill"
                    >
                      {tool.name}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button
                    onClick={() => handleOpenWorkflow(workflow)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    Open in Toolbox
                  </button>
                  <button
                    onClick={() => removeWorkflow(workflow.id)}
                    className="text-xs font-medium text-neutral-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
